// Du besoin à l'application (étape 2) : trois écrans, « Besoin », « Architecture » et « Impact », branchés sur
// POST /v0/cartes/{cid}/applications-cibles/essai (lecture seule : rien n'est écrit). L'application est saisie (collée)
// ou chargée depuis un fichier « ekera-application-cible » ; la conception par l'agent vient plus tard. Aucun calcul ici :
// tout chiffre vient de la réponse de l'API, qui vient du moteur. Le texte du besoin est une donnée, jamais une consigne.
// Rien n'est gardé : ni dans l'adresse, ni dans un stockage du navigateur (les variantes vivent en mémoire).
import { useCallback, useRef, useState, type ReactNode } from 'react';
import type { EffetApplication } from '../api/types';
import { useCarte, useEkera } from '../contexte';
import { Etiquette } from '../composants/communs';
import { type Application, jourBesoin, lireApplication, MAX_VARIANTES, MESSAGE_TROP_GROS, TAILLE_MAX_OCTETS } from '../domaine/applicationCible';
import { lienVers } from '../routeur';
import { EcranArchitecture } from './ApplicationArchitecture';
import { EcranImpact } from './ApplicationImpact';

export type EtatVariante = 'attente' | 'calcul' | 'ok' | 'erreur';

export interface VarianteApplication {
  cle: number;
  /** Lettre gardée depuis le chargement : écarter la variante A ne renomme pas la B. */
  lettre: string;
  application: Application;
  effet?: EffetApplication;
  etat: EtatVariante;
  erreur?: unknown;
}

type Etape = 'besoin' | 'architecture' | 'impact';
const ETAPES: { id: Etape; libelle: string }[] = [
  { id: 'besoin', libelle: '1 · Besoin' },
  { id: 'architecture', libelle: '2 · Architecture' },
  { id: 'impact', libelle: '3 · Impact' },
];

export const LETTRES = ['A', 'B', 'C'];

export function EcranApplication({ params }: { params: URLSearchParams }) {
  const { client, cid } = useEkera();
  const carte = useCarte();
  const brut = params.get('etape');
  const etape: Etape = brut === 'architecture' || brut === 'impact' ? brut : 'besoin';
  const [variantes, setVariantes] = useState<VarianteApplication[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const compteur = useRef(0);

  const ajouter = useCallback((application: Application) => {
    const cle = ++compteur.current;
    setVariantes((v) => {
      const lettre = LETTRES.find((l) => !v.some((x) => x.lettre === l));
      return v.length >= MAX_VARIANTES || !lettre ? v : [...v, { cle, lettre, application, etat: 'attente' }];
    });
    setActive(cle);
  }, []);
  // Écarter une variante la sort de la comparaison de cet écran ; rien n'est supprimé (rien n'a été écrit).
  const ecarter = useCallback((cle: number) => {
    setVariantes((v) => v.filter((x) => x.cle !== cle));
    setActive((a) => (a === cle ? null : a));
  }, []);
  const modifier = useCallback((cle: number, patch: Partial<VarianteApplication>) => {
    setVariantes((v) => v.map((x) => (x.cle === cle ? { ...x, ...patch } : x)));
  }, []);
  const calculer = useCallback(
    async (cle: number, application: Application) => {
      modifier(cle, { etat: 'calcul', erreur: undefined });
      try {
        const effet = await client.effetApplication(cid, application);
        modifier(cle, { etat: 'ok', effet });
      } catch (e) {
        modifier(cle, { etat: 'erreur', erreur: e, effet: undefined });
      }
    },
    [client, cid, modifier],
  );

  const courante = variantes.find((v) => v.cle === active) ?? variantes[0];
  return (
    <div className="pile">
      <p className="sous-titre">
        À partir d’un besoin du métier, une application cible est posée en calque sur la carte, sans rien y écrire ; le moteur
        d’Ekera calcule ce qu’elle changerait. Ekera ne génère ni ne pousse de code chez vous : vos équipes décident.
      </p>
      <nav className="onglets" aria-label="Du besoin à l’application">
        {ETAPES.map((e) => (
          <a key={e.id} href={lienVers('application', { etape: e.id === 'besoin' ? undefined : e.id })} aria-current={etape === e.id ? 'page' : undefined} className="onglet">
            {e.libelle}
          </a>
        ))}
      </nav>
      {variantes.length > 1 && etape !== 'impact' && (
        <div className="onglets" role="group" aria-label="Variante affichée">
          {variantes.map((v) => (
            <button key={v.cle} type="button" aria-pressed={courante?.cle === v.cle} onClick={() => setActive(v.cle)}>
              Variante {v.lettre}
            </button>
          ))}
        </div>
      )}
      {etape === 'besoin' && <EcranBesoin variantes={variantes} courante={courante} surAjout={ajouter} surEcart={ecarter} />}
      {etape === 'architecture' && <EcranArchitecture variante={courante} carte={carte.data} surCalcul={() => courante && void calculer(courante.cle, courante.application)} />}
      {etape === 'impact' && <EcranImpact variantes={variantes} carte={carte.data} surCalcul={(v) => void calculer(v.cle, v.application)} surEcart={ecarter} />}
      <p className="note">
        Calcul en lecture seule : la carte, le journal et les décisions ne changent pas. Le fichier et le texte du besoin restent
        dans cette page, jamais dans l’adresse ni dans un stockage du navigateur.
      </p>
    </div>
  );
}

function EcranBesoin({
  variantes,
  courante,
  surAjout,
  surEcart,
}: {
  variantes: VarianteApplication[];
  courante: VarianteApplication | undefined;
  surAjout: (a: Application) => void;
  surEcart: (cle: number) => void;
}) {
  const [texte, setTexte] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const plein = variantes.length >= MAX_VARIANTES;
  const charger = (contenu: string) => {
    const l = lireApplication(contenu);
    if ('erreur' in l) {
      setRefus(l.erreur);
      return;
    }
    setRefus(null);
    surAjout(l.application);
    setTexte('');
  };
  const surFichier = (f: File | undefined) => {
    if (!f) return;
    // Taille contrôlée avant toute lecture : un fichier trop gros n'est pas chargé en mémoire.
    if (f.size > TAILLE_MAX_OCTETS) {
      setRefus(MESSAGE_TROP_GROS);
      return;
    }
    const lecteur = new FileReader();
    lecteur.onload = () => charger(String(lecteur.result ?? ''));
    lecteur.onerror = () => setRefus('Le fichier n’a pas pu être lu.');
    lecteur.readAsText(f);
  };
  const b = courante?.application.besoin;
  return (
    <>
      <section className="panneau" aria-labelledby="t-besoin">
        <h2 id="t-besoin">Le besoin, en langage courant</h2>
        {b ? (
          <>
            <blockquote className="citation besoin-texte" data-testid="texte-besoin">
              <q>{b.texte}</q>
            </blockquote>
            <p className="note">
              Besoin <span className="num">{b.id}</span>, version {b.version}
              {b.demandeur ? ` · demandé par ${b.demandeur}` : ''}
              {b.exprime_le ? ` · exprimé le ${jourBesoin(b.exprime_le)}` : ''}. Ce texte est lu comme une donnée : il ne donne
              aucune consigne à Ekera.
            </p>
            <p>
              Application proposée : <b>{courante.application.nom}</b> <Etiquette ton="attention">proposée, non retenue</Etiquette>
            </p>
          </>
        ) : (
          <p className="muted">Aucune application cible n’est chargée. Chargez un fichier ou collez son contenu ci-dessous.</p>
        )}
      </section>

      <section className="panneau" aria-labelledby="t-charger">
        <h2 id="t-charger">Charger une application cible</h2>
        <p className="note">
          Fichier au format « ekera-application-cible » (64 Kio au plus). Jusqu’à {MAX_VARIANTES} variantes d’architecture
          peuvent être comparées à l’écran « Impact ».
        </p>
        <div className="champ">
          <label htmlFor="fichier-application">Fichier de l’application cible (JSON)</label>
          <input
            id="fichier-application"
            type="file"
            accept=".json,application/json"
            disabled={plein}
            onChange={(e) => {
              surFichier(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor="texte-application">… ou contenu collé</label>
          <textarea id="texte-application" className="num" rows={6} value={texte} disabled={plein} onChange={(e) => setTexte(e.target.value)} spellCheck={false} />
        </div>
        <div>
          <button type="button" className="btn principal" disabled={plein || texte.trim() === ''} onClick={() => charger(texte)}>
            Charger cette application
          </button>
        </div>
        {plein && <p className="note">Trois variantes sont déjà chargées : écartez-en une de la comparaison pour en charger une autre.</p>}
        {refus && (
          <p className="encadre erreur" role="alert">
            {refus}
          </p>
        )}
      </section>

      {variantes.length > 0 && (
        <section className="panneau" aria-labelledby="t-variantes">
          <h2 id="t-variantes">Variantes chargées</h2>
          <ul className="liste-points">
            {variantes.map((v) => (
              <li key={v.cle} className="ok">
                <b>
                  Variante {v.lettre} · {v.application.nom}
                </b>
                <small>
                  {(v.application.objets ?? []).length} objet(s) et {(v.application.liens ?? []).length} lien(s) proposés
                </small>
                <button type="button" className="btn petit" onClick={() => surEcart(v.cle)}>
                  Écarter de la comparaison
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panneau" aria-labelledby="t-venir">
        <h2 id="t-venir">Pas encore disponible</h2>
        <p className="note">
          Les versions successives du besoin et ce qui a changé de l’une à l’autre (étape 3), et les questions de clarification
          de l’agent (étape 4) ne sont pas encore proposées : l’API ne les fournit pas encore.
        </p>
      </section>
      <p>
        {courante ? (
          <a className="btn principal" href={lienVers('application', { etape: 'architecture' })}>
            Voir l’architecture proposée
          </a>
        ) : (
          <button type="button" className="btn principal" disabled>
            Voir l’architecture proposée
          </button>
        )}
      </p>
    </>
  );
}

export function Section({ id, titre, children }: { id: string; titre: string; children: ReactNode }) {
  return (
    <section className="panneau" aria-labelledby={id}>
      <h2 id={id}>{titre}</h2>
      {children}
    </section>
  );
}
